import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import { Announcement } from '../types';
import { announcementApi } from '../services/api';
import { useAuth } from "../auth/AuthContext";

const NewsCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  cursor: 'pointer',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const formatAnnouncementDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    return dateStr;
  }
};

const NewsList = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const itemsPerPage = 10;
  const { storeId } = useAuth();
  const navigate = useNavigate();

  // お知らせ取得
  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!storeId) {
        setError("店舗IDが利用できません");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await announcementApi.getStoreAnnouncements(storeId, {
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          only_unread: showUnreadOnly,
          sort_order: 'desc',
          sort_by: 'created_at'
        });
        
        setAnnouncements(response);
        // APIが総数を返さないため、取得件数から推測
        // 実際の実装では、APIレスポンスに総数も含めることを推奨
        const hasMore = response.length === itemsPerPage;
        setTotalPages(hasMore ? currentPage + 1 : currentPage);
        setLoading(false);
      } catch (err: any) {
        // エラーの詳細を判定
        if (err?.response?.status === 403) {
          setError("お知らせにアクセスする権限がありません");
        } else if (err?.response?.status === 404) {
          setError("お知らせが見つかりません");
        } else if (err?.response?.status >= 500) {
          setError("サーバーエラーが発生しました。しばらく時間をおいて再度お試しください。");
        } else {
          setError("お知らせの取得に失敗しました");
        }
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [storeId, currentPage, showUnreadOnly]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo(0, 0);
  };

  const handleUnreadFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowUnreadOnly(event.target.checked);
    setCurrentPage(1); // フィルタ変更時は1ページ目に戻す
  };

  const handleAnnouncementClick = (announcementId: string) => {
    navigate(`/announcements/${announcementId}`);
  };

  const handleMarkAllAsRead = async () => {
    if (!storeId) return;
    
    try {
      await announcementApi.markAllAsRead(storeId);
      // 一括既読後、データを再取得
      const response = await announcementApi.getStoreAnnouncements(storeId, {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        only_unread: showUnreadOnly,
        sort_order: 'desc',
        sort_by: 'created_at'
      });
      setAnnouncements(response);
    } catch (err: any) {
      // エラーが発生してもデータを再取得して画面を更新
      try {
        const response = await announcementApi.getStoreAnnouncements(storeId, {
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          only_unread: showUnreadOnly,
          sort_order: 'desc',
          sort_by: 'created_at'
        });
        setAnnouncements(response);
      } catch (refreshErr) {
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h6" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
          お知らせ
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h6" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        お知らせ
      </Typography>
      
      {/* フィルタとアクション */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={showUnreadOnly}
              onChange={handleUnreadFilterChange}
              size="small"
            />
          }
          label="未読のみ表示"
        />
        <Button 
          variant="outlined" 
          size="small" 
          onClick={handleMarkAllAsRead}
          disabled={announcements.length === 0}
        >
          すべて既読にする
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {announcements.length}件のお知らせ
      </Typography>

      {announcements.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {showUnreadOnly ? '未読のお知らせはありません' : 'お知らせはありません'}
        </Typography>
      ) : (
        <Box sx={{ mb: 4 }}>
          {announcements.map((announcement) => (
            <NewsCard key={announcement.id} onClick={() => handleAnnouncementClick(announcement.id)}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* 左側：カテゴリラベルと未読表示 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                    {announcement.category_name && (
                      <CategoryChip
                        label={announcement.category_name}
                        size="small"
                        sx={{ 
                          mb: 1,
                          backgroundColor: announcement.category_color || 'default',
                          color: 'white'
                        }}
                      />
                    )}
                    {!announcement.is_read && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'error.main',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      >
                        未読
                      </Typography>
                    )}
                    {announcement.is_pinned && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'warning.main',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          mt: 0.5
                        }}
                      >
                        重要
                      </Typography>
                    )}
                  </Box>

                  {/* 右側：タイトル、内容、日付 */}
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      sx={{ 
                        fontWeight: 'bold', 
                        mb: 1.5,
                        color: 'text.primary'
                      }}
                    >
                      {announcement.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ 
                        lineHeight: 1.6, 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {announcement.content}
                    </Typography>

                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        textAlign: 'right',
                        fontWeight: 'medium'
                      }}
                    >
                      {formatAnnouncementDate(announcement.created_at)} 掲載
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </NewsCard>
          ))}
        </Box>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="medium"
          />
        </Box>
      )}

      {/* ページ情報表示 */}
      {totalPages > 1 && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {currentPage}ページ目 / {totalPages}ページ中
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NewsList;